import { Package, BookOpen, ScrollText, Scale, Boxes, TrendingUp, Loader2, Plus, ShoppingCart, ShoppingBag, ArrowLeftRight, Search as SearchIcon, User as UserIcon, CheckCircle2, X, IndianRupee, ReceiptText, ScanLine, Download, FileSpreadsheet, FileText, Mail, Send, Tag, Trash2, AlertTriangle, Image as ImageIcon } from "lucide-react";
import React, { useEffect, useMemo, useState, createContext, useContext } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import BarcodeScanner from "@/components/BarcodeScanner";
import TagGenerator from "@/components/TagGenerator";
import CashFlowChart from "@/components/CashFlowChart";

// -----------------------------------------------------------------------------
// Currency helpers — amounts are stored as integer "minor units" (paise/cents)
// on the backend for accuracy. The user picks a display currency and enters
// amounts in major units (e.g. rupees, dollars). We convert on submit.
// -----------------------------------------------------------------------------
const CurrencyCtx = createContext({ code: "INR", symbol: "₹", decimals: 2 });

// Comprehensive unit catalogue for the Business Ledger. Grouped for the picker.
// Users can also enter a custom unit if their trade needs something niche.
const UNIT_GROUPS = [
  {
    label: "Count / Pieces",
    units: ["pcs", "unit", "each", "pair", "dozen", "gross", "set", "kit", "lot"],
  },
  {
    label: "Weight",
    units: ["mg", "g", "kg", "tonne", "quintal", "oz", "lb", "carat", "tola"],
  },
  {
    label: "Volume / Liquid",
    units: ["mL", "L", "kL", "cc", "m³", "fl oz", "pint", "quart", "gallon", "barrel", "drum"],
  },
  {
    label: "Length",
    units: ["mm", "cm", "m", "km", "in", "ft", "yd", "mile"],
  },
  {
    label: "Area",
    units: ["sqft", "sqm", "sqyd", "sqin", "acre", "hectare", "guntha", "bigha", "cent"],
  },
  {
    label: "Packaging",
    units: ["box", "carton", "crate", "packet", "pouch", "bag", "sack", "roll", "sheet", "bundle", "bottle", "can", "jar", "tin", "tube", "sachet"],
  },
  {
    label: "Time / Service",
    units: ["minute", "hour", "day", "night", "week", "month", "quarter", "year", "session", "visit", "trip", "call"],
  },
  {
    label: "Food / Bakery",
    units: ["slice", "loaf", "plate", "bowl", "cup", "glass", "scoop", "portion", "combo", "meal", "thali"],
  },
  {
    label: "Digital / Software",
    units: ["MB", "GB", "TB", "license", "seat", "user", "workspace", "download", "impression", "click", "credit"],
  },
  {
    label: "Textiles / Construction",
    units: ["metre-fabric", "yard-fabric", "sqft-tile", "cubic-metre", "brick", "bag-cement", "bar-steel"],
  },
];
const ALL_UNITS = UNIT_GROUPS.flatMap(g => g.units);
const CUSTOM_UNIT = "__custom__";

const toMinor = (majorStr, decimals = 2) => {
  const n = parseFloat(majorStr);
  if (!isFinite(n) || n < 0) return 0;
  return Math.round(n * Math.pow(10, decimals));
};

const fromMinor = (minor, decimals = 2) => {
  if (minor == null || !isFinite(minor)) return "";
  return (minor / Math.pow(10, decimals)).toFixed(decimals);
};

const formatMoney = (minor, cur = { symbol: "₹", decimals: 2 }) => {
  const d = cur.decimals ?? 2;
  const val = (minor || 0) / Math.pow(10, d);
  const parts = val.toFixed(d).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${cur.symbol}${parts.join(".")}`;
};

const useCurrency = () => useContext(CurrencyCtx);

export default function ERP() {
  const { user } = useAuth();
  const [tab, setTab] = useState(window.location.hash.replace("#", "") || "products");
  const [currency, setCurrency] = useState({ code: "INR", symbol: "₹", decimals: 2, name: "Indian Rupee" });
  const [supported, setSupported] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
    const onHash = () => setTab(window.location.hash.replace("#", "") || "products");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (!user) { setLoadingSettings(false); return; }
    api.get("/erp/settings")
      .then(r => { setCurrency(r.data.settings); setSupported(r.data.supported || []); })
      .catch(() => {})
      .finally(() => setLoadingSettings(false));
  }, [user]);

  const changeCurrency = async (code) => {
    const prev = currency;
    const next = supported.find(s => s.code === code) || prev;
    setCurrency(next);   // optimistic
    try {
      await api.put("/erp/settings", { currency: code });
      toast.success(`Currency changed to ${next.code} (${next.symbol})`);
    } catch (e) {
      setCurrency(prev);
      toast.error(e?.response?.data?.detail || "Failed to update currency");
    }
  };

  const switchTab = (t) => { window.location.hash = t; setTab(t); };

  if (!user) return <div className="p-12 text-center text-stone-500">Please sign in to use ERP.</div>;

  const TABS = [
    { k: "products", l: "Products", Icon: Package },
    { k: "sale", l: "Record sale", Icon: ShoppingCart },
    { k: "purchase", l: "Record purchase", Icon: ShoppingBag },
    { k: "p2p", l: "P2P Bills", Icon: ArrowLeftRight },
    { k: "daybook", l: "Day book", Icon: BookOpen },
    { k: "ledger", l: "Ledger", Icon: ScrollText },
    { k: "categories", l: "Categories", Icon: Tag },
    { k: "balance", l: "Balance sheet", Icon: Scale },
    { k: "inventory", l: "Inventory", Icon: Boxes },
    { k: "holdings", l: "Holdings", Icon: TrendingUp },
  ];

  return (
    <CurrencyCtx.Provider value={currency}>
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-6" data-testid="erp-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900 mb-1">Business ledger</h1>
            <p className="text-stone-500 text-sm">Products, double-entry accounts, and full financial reports.</p>
          </div>
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-2xl px-3 py-2" data-testid="erp-currency-picker">
            <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Currency</span>
            <Select value={currency.code} onValueChange={changeCurrency} disabled={loadingSettings || !supported.length}>
              <SelectTrigger className="rounded-lg h-8 border-stone-200 w-40" data-testid="erp-currency-select">
                <SelectValue>
                  <span className="font-mono font-bold">{currency.symbol}</span>
                  <span className="ml-2 text-xs text-stone-500">{currency.code}</span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {supported.map(c => (
                  <SelectItem key={c.code} value={c.code} data-testid={`erp-currency-opt-${c.code}`}>
                    <span className="font-mono font-bold w-8 inline-block">{c.symbol}</span>
                    <span className="ml-1 font-bold">{c.code}</span>
                    <span className="ml-2 text-xs text-stone-500">{c.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-1 mb-5 border-b border-stone-200 overflow-x-auto">
          {TABS.map(t => {
            const active = tab === t.k;
            return (
              <button key={t.k} onClick={() => switchTab(t.k)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold whitespace-nowrap border-b-2 -mb-px ${active ? "text-emerald-700 border-emerald-700" : "text-stone-500 border-transparent hover:text-stone-800"}`}
                data-testid={`erp-tab-${t.k}`}>
                <t.Icon size={12} /> {t.l}
              </button>
            );
          })}
        </div>

        {tab === "products" && <ProductsTab />}
        {tab === "sale" && <SaleTab />}
        {tab === "purchase" && <PurchaseTab />}
        {tab === "p2p" && <P2PTab />}
        {tab === "daybook" && <DaybookTab />}
        {tab === "ledger" && <LedgerTab />}
        {tab === "categories" && <CategoriesTab />}
        {tab === "balance" && <BalanceTab />}
        {tab === "inventory" && <InventoryTab />}
        {tab === "holdings" && <HoldingsTab />}
      </div>
    </CurrencyCtx.Provider>
  );
}

function ProductsTab() {
  const cur = useCurrency();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scannedImage, setScannedImage] = useState(null);
  const [f, setF] = useState({ name: "", sku: "", unit: "pcs", customUnit: "", cost: "", sell: "", tax_pct: "0", opening_stock: "0" });

  const refresh = () => { setLoading(true); api.get("/erp/products").then(r => setProducts(r.data)).finally(() => setLoading(false)); };
  useEffect(refresh, []);

  const resolveUnit = () => f.unit === CUSTOM_UNIT ? (f.customUnit.trim() || "unit") : f.unit;

  const handleScanned = (data) => {
    // Prefill form from barcode lookup. Cost/sell not returned (barcodes don't carry price)
    // — the user still fills them. We do prefill name, unit_hint and SKU=barcode.
    const unitHint = data.unit_hint && ALL_UNITS.includes(data.unit_hint) ? data.unit_hint : "pcs";
    const displayName = data.brand && !data.name.toLowerCase().includes(data.brand.toLowerCase())
      ? `${data.brand} — ${data.name}` : data.name;
    setF(prev => ({
      ...prev,
      name: displayName,
      sku: data.code,
      unit: unitHint,
      customUnit: "",
    }));
    setScannedImage(data.image_url || null);
    setCreating(true);
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/erp/products", {
        name: f.name,
        sku: f.sku,
        unit: resolveUnit(),
        cost_paise: toMinor(f.cost, cur.decimals),
        sell_paise: toMinor(f.sell, cur.decimals),
        opening_stock: parseInt(f.opening_stock) || 0,
        tax_pct: parseFloat(f.tax_pct) || 0,
      });
      toast.success("Product added");
      setCreating(false); setScannedImage(null); setF({ name: "", sku: "", unit: "pcs", customUnit: "", cost: "", sell: "", tax_pct: "0", opening_stock: "0" }); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const step = cur.decimals > 0 ? `0.${"0".repeat(Math.max(0, cur.decimals - 1))}1` : "1";

  return (
    <div className="space-y-4" data-testid="erp-products">
      <BarcodeScanner open={scanning} onClose={() => setScanning(false)} onScanned={handleScanned} />
      {!creating ? (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setCreating(true)} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="erp-product-new"><Plus size={14} className="mr-1.5" /> Add product</Button>
          <Button type="button" onClick={() => setScanning(true)} variant="outline" className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="erp-product-scan">
            <ScanLine size={14} className="mr-1.5" /> Scan barcode
          </Button>
        </div>
      ) : (
        <form onSubmit={create} className="bg-white border border-stone-200 rounded-2xl p-5 grid md:grid-cols-3 gap-3">
          {scannedImage && (
            <div className="md:col-span-3 flex items-center gap-3 -mt-1 mb-1 p-3 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="erp-scanned-preview">
              <img loading="lazy" src={scannedImage} alt="scanned product" className="w-12 h-12 rounded-lg object-cover bg-white border border-emerald-100" />
              <div className="text-[11px] text-emerald-900">Prefilled from barcode <b className="font-mono">{f.sku}</b>. Adjust anything below and set your cost/sell price.</div>
            </div>
          )}
          <Input required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Name" className="rounded-xl md:col-span-2" data-testid="erp-p-name" />
          <Input value={f.sku} onChange={e => setF({ ...f, sku: e.target.value })} placeholder="SKU (auto if blank)" className="rounded-xl" data-testid="erp-p-sku" />
          <UnitPicker
            value={f.unit}
            customValue={f.customUnit}
            onChange={(u, custom) => setF({ ...f, unit: u, customUnit: custom ?? f.customUnit })}
            testId="erp-p-unit"
          />
          <MoneyInput value={f.cost} onChange={v => setF({ ...f, cost: v })} placeholder={`Cost per unit (${cur.symbol})`} step={step} testId="erp-p-cost" />
          <MoneyInput value={f.sell} onChange={v => setF({ ...f, sell: v })} placeholder={`Sell price (${cur.symbol})`} step={step} testId="erp-p-sell" />
          <Input type="number" min="0" value={f.opening_stock} onChange={e => setF({ ...f, opening_stock: e.target.value })} placeholder="Opening stock" className="rounded-xl" data-testid="erp-p-stock" />
          <Input type="number" step="0.1" value={f.tax_pct} onChange={e => setF({ ...f, tax_pct: e.target.value })} placeholder="Tax %" className="rounded-xl" data-testid="erp-p-tax" />
          <div className="md:col-span-3 flex gap-2 justify-end items-center flex-wrap">
            <span className="text-[11px] text-stone-500 mr-auto">Enter amounts in <b className="font-mono">{cur.symbol} {cur.code}</b> (major units, not minor)</span>
            <Button type="button" variant="ghost" onClick={() => setScanning(true)} className="rounded-full text-emerald-700 hover:bg-emerald-50" data-testid="erp-p-scan"><ScanLine size={14} className="mr-1.5" /> Scan</Button>
            <Button type="button" variant="outline" onClick={() => { setCreating(false); setScannedImage(null); }} className="rounded-full">Cancel</Button>
            <Button type="submit" className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="erp-p-save">Save</Button>
          </div>
        </form>
      )}
      {loading ? <Loader2 className="animate-spin mx-auto text-stone-400" /> : (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="erp-products-table">
            <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
              <tr><th className="px-3 py-2 text-left">Name</th><th className="text-left">SKU</th><th className="text-right">Stock</th><th className="text-right">Cost</th><th className="text-right">Sell</th></tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {products.map(p => (
                <tr key={p.product_id} className="hover:bg-stone-50">
                  <td className="px-3 py-2 font-bold text-stone-900">{p.name}</td>
                  <td className="text-stone-500 font-mono text-xs">{p.sku}</td>
                  <td className="text-right font-mono">{p.stock} {p.unit}</td>
                  <td className="text-right font-mono text-stone-600">{formatMoney(p.cost_paise, cur)}</td>
                  <td className="text-right font-mono font-bold text-emerald-700">{formatMoney(p.sell_paise, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {products.length === 0 && <div className="p-6 text-center text-stone-500 text-sm">No products yet.</div>}
        </div>
      )}
    </div>
  );
}

/** MoneyInput: currency-symbol-prefixed input that accepts major units (e.g. rupees). */
function MoneyInput({ value, onChange, placeholder, step = "0.01", testId }) {
  const cur = useCurrency();
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 font-mono text-sm pointer-events-none">{cur.symbol}</span>
      <Input
        type="number"
        min="0"
        step={step}
        inputMode="decimal"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-xl pl-8"
        data-testid={testId}
      />
    </div>
  );
}

/**
 * UnitPicker: grouped select of common trade units with a "Custom…" escape hatch
 * for anything niche. Value is either a string in ALL_UNITS or the sentinel
 * CUSTOM_UNIT (in which case customValue holds the free-text override).
 */
function UnitPicker({ value, customValue, onChange, testId }) {
  const isCustom = value === CUSTOM_UNIT;
  return (
    <div className="space-y-2">
      <Select value={value} onValueChange={(v) => onChange(v, v === CUSTOM_UNIT ? customValue : "")}>
        <SelectTrigger className="rounded-xl" data-testid={testId}>
          <SelectValue placeholder="Unit (pcs, kg, L…)" />
        </SelectTrigger>
        <SelectContent className="max-h-80">
          {UNIT_GROUPS.map(g => (
            <div key={g.label}>
              <div className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-stone-400">{g.label}</div>
              {g.units.map(u => (
                <SelectItem key={u} value={u} data-testid={`erp-unit-opt-${u}`}>{u}</SelectItem>
              ))}
            </div>
          ))}
          <div className="border-t border-stone-100 mt-1">
            <SelectItem value={CUSTOM_UNIT} data-testid="erp-unit-opt-custom">
              <span className="italic text-stone-500">+ Custom unit…</span>
            </SelectItem>
          </div>
        </SelectContent>
      </Select>
      {isCustom && (
        <Input
          autoFocus
          value={customValue}
          onChange={e => onChange(CUSTOM_UNIT, e.target.value)}
          placeholder="e.g. carboy, pallet, running-metre"
          className="rounded-xl"
          data-testid={`${testId}-custom-input`}
        />
      )}
    </div>
  );
}

function SaleTab() { return <TxnForm kind="sale" />; }
function PurchaseTab() { return <TxnForm kind="purchase" />; }

function TxnForm({ kind }) {
  const cur = useCurrency();
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([{ product_id: "", quantity: 1 }]);
  const [paymentAccount, setPaymentAccount] = useState("cash");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/erp/products").then(r => setProducts(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const validLines = lines.filter(l => l.product_id && l.quantity > 0);
    if (!validLines.length) return toast.error("Add at least one line");
    setSaving(true);
    try {
      const r = await api.post(`/erp/${kind}`, { lines: validLines.map(l => ({ ...l, quantity: parseInt(l.quantity) })), payment_account: paymentAccount });
      toast.success(kind === "sale" ? `Sale booked · Revenue ${formatMoney(r.data.revenue_paise, cur)}, Gross profit ${formatMoney(r.data.gross_profit_paise, cur)}` : `Purchase booked · Total ${formatMoney(r.data.total_paise, cur)}`);
      setLines([{ product_id: "", quantity: 1 }]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3" data-testid={`erp-${kind}-form`}>
      <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">{kind === "sale" ? "Record a sale" : "Record a purchase"}</div>
      {lines.map((l, idx) => (
        <div key={idx} className="grid md:grid-cols-6 gap-2 items-center">
          <Select value={l.product_id} onValueChange={v => setLines(ll => ll.map((x, i) => i === idx ? { ...x, product_id: v } : x))}>
            <SelectTrigger className="rounded-xl md:col-span-4" data-testid={`erp-${kind}-product-${idx}`}><SelectValue placeholder="Product" /></SelectTrigger>
            <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name} · stock {p.stock}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" min="1" value={l.quantity} onChange={e => setLines(ll => ll.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))} className="rounded-xl" data-testid={`erp-${kind}-qty-${idx}`} />
          <Button type="button" variant="outline" onClick={() => setLines(ll => ll.filter((_, i) => i !== idx))} disabled={lines.length === 1} className="rounded-full">Remove</Button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2 items-end">
        <Button type="button" variant="outline" onClick={() => setLines(ll => [...ll, { product_id: "", quantity: 1 }])} className="rounded-full text-xs" data-testid={`erp-${kind}-add-line`}>+ Add line</Button>
        <Select value={paymentAccount} onValueChange={setPaymentAccount}>
          <SelectTrigger className="rounded-xl w-40" data-testid={`erp-${kind}-account`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="bank">Bank</SelectItem>
            <SelectItem value={kind === "sale" ? "debtors" : "creditors"}>{kind === "sale" ? "On credit (debtors)" : "On credit (creditors)"}</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" disabled={saving} className={`rounded-full ml-auto ${kind === "sale" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`} data-testid={`erp-${kind}-submit`}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : kind === "sale" ? "Book sale" : "Book purchase"}
        </Button>
      </div>
    </form>
  );
}

function DaybookTab() {
  const cur = useCurrency();
  const [entries, setEntries] = useState([]);
  useEffect(() => { api.get("/erp/reports/daybook").then(r => setEntries(r.data)).catch(() => {}); }, []);
  // Expand each journal entry into TWO rows: the debit line and the credit line.
  // Standard double-entry bookkeeping — every amount appears on BOTH sides, so
  // the running totals of Debit column = Credit column.
  const rows = useMemo(() => {
    const out = [];
    entries.forEach(e => {
      out.push({ ...e, side: "debit",  account: e.debit_code,  key: `${e.journal_id}-dr` });
      out.push({ ...e, side: "credit", account: e.credit_code, key: `${e.journal_id}-cr` });
    });
    return out;
  }, [entries]);
  const totals = useMemo(() => {
    const dr = entries.reduce((s, e) => s + (e.amount_paise || 0), 0);
    return { dr, cr: dr };   // by construction, always balanced
  }, [entries]);
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="erp-daybook">
      <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between gap-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Day book · {entries.length} entries · {rows.length} lines</div>
        <ExportButtons endpoint="/erp/reports/daybook" filename="daybook" testIdPrefix="erp-daybook-export" />
      </div>
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
          <tr>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="text-left">Particulars</th>
            <th className="text-left">Account</th>
            <th className="text-right pr-3">Debit</th>
            <th className="text-right pr-3">Credit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {rows.map(r => (
            <tr key={r.key} className={`hover:bg-stone-50 ${r.side === "debit" ? "bg-emerald-50/30" : ""}`} data-testid={`daybook-row-${r.side}`}>
              <td className="px-3 py-2 text-xs text-stone-500 font-mono">{r.side === "debit" ? r.date : ""}</td>
              <td className="text-stone-700">{r.side === "debit" ? r.description : <span className="text-stone-400 pl-4">to {r.credit_code}</span>}</td>
              <td className={`font-bold text-xs capitalize ${r.side === "debit" ? "text-emerald-700" : "text-rose-700"}`}>{r.account}</td>
              <td className="text-right pr-3 font-mono font-bold text-emerald-800">{r.side === "debit" ? formatMoney(r.amount_paise, cur) : ""}</td>
              <td className="text-right pr-3 font-mono font-bold text-rose-800">{r.side === "credit" ? formatMoney(r.amount_paise, cur) : ""}</td>
            </tr>
          ))}
        </tbody>
        {entries.length > 0 && (
          <tfoot className="bg-stone-100 border-t-2 border-stone-300">
            <tr>
              <td colSpan={3} className="px-3 py-2 text-[11px] uppercase tracking-widest font-black text-stone-600">Totals</td>
              <td className="text-right pr-3 font-mono font-black text-emerald-900" data-testid="daybook-total-debit">{formatMoney(totals.dr, cur)}</td>
              <td className="text-right pr-3 font-mono font-black text-rose-900" data-testid="daybook-total-credit">{formatMoney(totals.cr, cur)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      {entries.length === 0 && <div className="p-8 text-center text-stone-500 text-sm">No transactions yet.</div>}
    </div>
  );
}

/** ExportButtons — downloads {endpoint}.csv and {endpoint}.pdf via authenticated blob fetch. */
function ExportButtons({ endpoint, filename, testIdPrefix, params }) {
  const [busy, setBusy] = useState(null);   // "csv" | "pdf" | null

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const r = await api.get(`${endpoint}.${fmt}`, { responseType: "blob", params: params || undefined });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to export ${fmt.toUpperCase()}`);
    } finally { setBusy(null); }
  };

  return (
    <div className="flex gap-1.5">
      <button
        type="button"
        onClick={() => download("csv")}
        disabled={!!busy}
        className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border border-stone-200 hover:border-emerald-500 hover:text-emerald-700 disabled:opacity-60"
        data-testid={`${testIdPrefix}-csv`}
      >
        {busy === "csv" ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
        CSV
      </button>
      <button
        type="button"
        onClick={() => download("pdf")}
        disabled={!!busy}
        className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border border-stone-200 hover:border-red-500 hover:text-red-700 disabled:opacity-60"
        data-testid={`${testIdPrefix}-pdf`}
      >
        {busy === "pdf" ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
        PDF
      </button>
    </div>
  );
}

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** ReportSubscriptionsPanel — subscribe to multiple email reports with different cadences. */
function ReportSubscriptionsPanel() {
  const [kinds, setKinds] = useState([]);
  const [subs, setSubs]   = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [k, s] = await Promise.all([
        api.get("/reports/kinds"),
        api.get("/reports/subscriptions"),
      ]);
      setKinds(k.data.kinds || []);
      const byKind = {};
      for (const sub of (s.data.subscriptions || [])) byKind[sub.kind] = sub;
      setSubs(byKind);
    } catch (_) {} finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  if (loading) return <div className="bg-white border border-stone-200 rounded-2xl p-4 text-center text-sm text-stone-500"><Loader2 className="inline animate-spin mr-1" size={14} /> Loading…</div>;

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-4 space-y-3" data-testid="erp-report-subs">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 grid place-items-center rounded-xl bg-emerald-100 text-emerald-700 shrink-0"><Mail size={16} /></div>
        <div className="flex-1">
          <div className="font-bold text-stone-900 text-sm">Email me reports on a schedule</div>
          <div className="text-[11px] text-stone-600">Pick the cadence per report — Balance Sheet, Cash Flow, Day-book. Delivered as a PDF attachment.</div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {kinds.map(k => (
          <ReportKindCard key={k.id} kindMeta={k} sub={subs[k.id]} onChanged={refresh} />
        ))}
      </div>
    </div>
  );
}

function ReportKindCard({ kindMeta, sub, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    email: sub?.email || "",
    hour_utc: sub?.hour_utc ?? 3,
    day_of_week: sub?.day_of_week ?? 0,
    day_of_month: sub?.day_of_month ?? 1,
  });
  useEffect(() => {
    if (sub) setForm({
      email: sub.email || "",
      hour_utc: sub.hour_utc ?? 3,
      day_of_week: sub.day_of_week ?? 0,
      day_of_month: sub.day_of_month ?? 1,
    });
  }, [sub?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (enabled) => {
    setBusy(true);
    try {
      const payload = { enabled, email: form.email || undefined, hour_utc: form.hour_utc };
      if (kindMeta.cadence === "weekly")  payload.day_of_week  = form.day_of_week;
      if (kindMeta.cadence === "monthly") payload.day_of_month = form.day_of_month;
      await api.put(`/reports/subscriptions/${kindMeta.id}`, payload);
      toast.success(enabled ? `${kindMeta.title} scheduled ✓` : `${kindMeta.title} paused`);
      setExpanded(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post(`/reports/subscriptions/${kindMeta.id}/send-now`);
      toast.success(`Test ${kindMeta.title} queued`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally { setSending(false); }
  };

  const enabled = !!sub?.enabled;
  const cadenceLabel =
    kindMeta.cadence === "daily"
      ? `daily at ${String(form.hour_utc).padStart(2, "0")}:00 UTC`
      : kindMeta.cadence === "weekly"
        ? `every ${DOW_LABELS[sub?.day_of_week ?? form.day_of_week]} at ${String(form.hour_utc).padStart(2, "0")}:00 UTC`
        : `monthly on day ${sub?.day_of_month ?? form.day_of_month} at ${String(form.hour_utc).padStart(2, "0")}:00 UTC`;

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3" data-testid={`erp-report-sub-${kindMeta.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">{kindMeta.cadence}</div>
          <div className="font-bold text-stone-900 text-sm truncate">{kindMeta.title}</div>
          <div className="text-[11px] text-stone-500">{kindMeta.hint}</div>
        </div>
        <span className={`shrink-0 w-2.5 h-2.5 rounded-full mt-1 ${enabled ? "bg-emerald-500" : "bg-stone-300"}`} title={enabled ? "Active" : "Paused"} />
      </div>
      <div className="mt-2 text-[11px] text-stone-600">
        {enabled ? <>Sending <b>{cadenceLabel}</b> to <b>{sub.email}</b></> : "Not scheduled."}
        {sub?.last_sent_at && <span className="ml-1 text-stone-400 font-mono">· last sent {sub.last_sent_at.slice(0, 10)}</span>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {sub && (
          <button type="button" onClick={sendNow} disabled={sending}
            className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border border-emerald-200 text-emerald-700 hover:border-emerald-500 disabled:opacity-60"
            data-testid={`erp-report-sub-${kindMeta.id}-send-now`}>
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Test send
          </button>
        )}
        <button type="button" onClick={() => setExpanded(v => !v)}
          className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 px-2 py-1"
          data-testid={`erp-report-sub-${kindMeta.id}-configure`}>
          {enabled ? "Change" : "Set up"}
        </button>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-stone-100 space-y-2" data-testid={`erp-report-sub-${kindMeta.id}-form`}>
          <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="Delivery email (defaults to your account email)"
            className="rounded-xl" data-testid={`erp-report-sub-${kindMeta.id}-email`} />
          <div className="grid grid-cols-2 gap-2">
            {kindMeta.cadence === "weekly" && (
              <Select value={String(form.day_of_week)} onValueChange={v => setForm({ ...form, day_of_week: parseInt(v) })}>
                <SelectTrigger className="rounded-xl" data-testid={`erp-report-sub-${kindMeta.id}-dow`}><SelectValue /></SelectTrigger>
                <SelectContent>{DOW_LABELS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {kindMeta.cadence === "monthly" && (
              <Select value={String(form.day_of_month)} onValueChange={v => setForm({ ...form, day_of_month: parseInt(v) })}>
                <SelectTrigger className="rounded-xl" data-testid={`erp-report-sub-${kindMeta.id}-dom`}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <SelectItem key={d} value={String(d)}>Day {d}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={String(form.hour_utc)} onValueChange={v => setForm({ ...form, hour_utc: parseInt(v) })}>
              <SelectTrigger className="rounded-xl" data-testid={`erp-report-sub-${kindMeta.id}-hour`}><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {Array.from({ length: 24 }, (_, h) => (
                  <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00 UTC</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {kindMeta.cadence === "daily" && <div />}
          </div>
          <div className="text-[10px] text-stone-500">IST ≈ UTC+5:30. 03:00 UTC = 08:30 IST.</div>
          <div className="flex gap-2 justify-end">
            {enabled && (
              <Button type="button" variant="outline" onClick={() => save(false)} disabled={busy}
                className="rounded-full text-red-700 border-red-200 hover:bg-red-50"
                data-testid={`erp-report-sub-${kindMeta.id}-disable`}>Pause</Button>
            )}
            <Button type="button" onClick={() => save(true)} disabled={busy}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700"
              data-testid={`erp-report-sub-${kindMeta.id}-enable`}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : enabled ? "Save changes" : "Turn on"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function LedgerTab() {
  const cur = useCurrency();
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/erp/accounts").then(r => setAccounts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!selected) return;
    api.get(`/erp/reports/ledger?account_code=${selected}`).then(r => setData(r.data)).catch(() => {});
  }, [selected]);
  return (
    <div className="space-y-3" data-testid="erp-ledger">
      {/* Product-tag generator + QR scanner. Sits above the account picker so
          stockists can quickly print a tag or scan an existing one before
          picking the ledger account to reconcile against. */}
      <TagGenerator />

      <div className="flex items-center gap-3">
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="rounded-xl w-72" data-testid="erp-ledger-account"><SelectValue placeholder="Pick an account" /></SelectTrigger>
          <SelectContent>{accounts.map(a => <SelectItem key={a.account_id} value={a.code}>{a.name} · {a.type}</SelectItem>)}</SelectContent>
        </Select>
        {selected && (
          <ExportButtons
            endpoint={`/erp/reports/ledger`}
            params={{ account_code: selected }}
            filename={`ledger-${selected}`}
            testIdPrefix="erp-ledger-export"
          />
        )}
      </div>
      {data && (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex justify-between items-baseline">
            <div className="font-bold text-stone-900">{data.account.name} <span className="text-xs text-stone-500 capitalize">({data.account.type})</span></div>
            <div className="text-sm font-mono font-bold">Closing: {formatMoney(Math.abs(data.closing_balance_paise), cur)}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
              <tr><th className="px-3 py-2 text-left">Date</th><th className="text-left">Description</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th></tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.entries.map(e => (
                <tr key={e.journal_id}>
                  <td className="px-3 py-2 text-xs text-stone-500 font-mono">{e.date}</td>
                  <td>{e.description}</td>
                  <td className="text-right font-mono text-emerald-700">{e.side === "debit" ? formatMoney(e.amount_paise, cur) : ""}</td>
                  <td className="text-right font-mono text-rose-700">{e.side === "credit" ? formatMoney(e.amount_paise, cur) : ""}</td>
                  <td className="text-right font-mono font-bold">{formatMoney(Math.abs(e.running_balance_paise), cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BalanceGroup({ title, rows, sum }) {
  const cur = useCurrency();
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-2 bg-stone-50 text-[11px] uppercase tracking-widest font-bold text-stone-500">{title}</div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-stone-100">
          {rows.map(a => (<tr key={a.account_id}><td className="px-4 py-2 text-stone-800">{a.name}</td><td className="text-right font-mono px-4">{formatMoney(Math.abs(a.balance_paise), cur)}</td></tr>))}
          <tr className="bg-stone-100 font-bold"><td className="px-4 py-2">Total {title}</td><td className="text-right font-mono px-4">{formatMoney(sum, cur)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

// -----------------------------------------------------------------------------
// CategoriesTab — manage user's custom accounting categories.
// Default categories are read-only; custom ones can be added / deleted (only
// if balance is 0 and no journal entries reference them).
// -----------------------------------------------------------------------------
const CATEGORY_TYPES = [
  { id: "expense",   label: "Expense",   hint: "Where money goes (e.g. Subscriptions, Travel)" },
  { id: "income",    label: "Income",    hint: "Where money comes from (e.g. Freelance, Rent received)" },
  { id: "asset",     label: "Asset",     hint: "Things you own (e.g. Petty cash, PayPal wallet)" },
  { id: "liability", label: "Liability", hint: "What you owe (e.g. Credit card, Loan)" },
  { id: "equity",    label: "Equity",    hint: "Owner capital / retained earnings" },
];

function CategoriesTab() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ name: "", type: "expense" });
  const [saving, setSaving] = useState(false);

  const refresh = () => { setLoading(true); api.get("/erp/accounts").then(r => setAccounts(r.data)).finally(() => setLoading(false)); };
  useEffect(refresh, []);

  const create = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/erp/accounts", { name: f.name.trim(), type: f.type });
      toast.success(`Category "${f.name}" added ✓`);
      setF({ name: "", type: f.type });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (code, name) => {
    if (!window.confirm(`Delete category "${name}"? This only works when its balance is 0 and it has no history.`)) return;
    try {
      await api.delete(`/erp/accounts/${code}`);
      toast.success(`Deleted "${name}"`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <Loader2 className="animate-spin mx-auto text-stone-400" />;

  return (
    <div className="space-y-4" data-testid="erp-categories">
      <form onSubmit={create} className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">Add a category</div>
        <p className="text-xs text-stone-500 -mt-1">Create custom accounting buckets so your cash-flow breakdowns match how you actually think about spending.</p>
        <div className="grid md:grid-cols-3 gap-2">
          <Input
            required value={f.name}
            onChange={e => setF({ ...f, name: e.target.value })}
            placeholder="Category name (e.g. Subscriptions, Travel)"
            className="rounded-xl md:col-span-2"
            data-testid="erp-cat-name"
          />
          <Select value={f.type} onValueChange={v => setF({ ...f, type: v })}>
            <SelectTrigger className="rounded-xl" data-testid="erp-cat-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORY_TYPES.map(t => (
                <SelectItem key={t.id} value={t.id}>
                  <div><b>{t.label}</b><div className="text-[10px] text-stone-500">{t.hint}</div></div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="erp-cat-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} className="mr-1" /> Add category</>}
          </Button>
        </div>
      </form>

      {CATEGORY_TYPES.map(t => {
        const rows = accounts.filter(a => a.type === t.id);
        if (!rows.length) return null;
        return (
          <div key={t.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={`erp-cat-group-${t.id}`}>
            <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-bold text-stone-500">
              {t.label} · {rows.length}
            </div>
            <ul className="divide-y divide-stone-100">
              {rows.map(a => (
                <li key={a.account_id} className="px-5 py-2.5 flex items-center gap-3 hover:bg-stone-50" data-testid={`erp-cat-row-${a.code}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-stone-900 truncate">{a.name}</div>
                    <div className="text-[11px] text-stone-500 font-mono">{a.code}</div>
                  </div>
                  {a.is_custom ? (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5">Custom</span>
                  ) : (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-stone-100 text-stone-500 border border-stone-200 rounded-full px-1.5 py-0.5">Default</span>
                  )}
                  {a.is_custom && (
                    <button
                      onClick={() => remove(a.code, a.name)}
                      className="text-stone-400 hover:text-red-600 transition-colors"
                      aria-label={`Delete ${a.name}`}
                      data-testid={`erp-cat-delete-${a.code}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function BalanceTab() {
  const cur = useCurrency();
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/erp/reports/balance-sheet").then(r => setB(r.data)).catch(() => {}); }, []);
  if (!b) return <Loader2 className="animate-spin mx-auto text-stone-400" />;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="erp-balance">
      <div className="md:col-span-2 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Balance sheet</div>
        <ExportButtons endpoint="/erp/reports/balance-sheet" filename="balance-sheet" testIdPrefix="erp-balance-export" />
      </div>
      <div className="md:col-span-2">
        <CashFlowChart />
      </div>
      <div className="md:col-span-2">
        <ReportSubscriptionsPanel />
      </div>
      <BalanceGroup title="Assets" rows={b.groups.asset} sum={b.totals.assets_paise} />
      <div className="space-y-4">
        <BalanceGroup title="Liabilities" rows={b.groups.liability} sum={b.totals.liabilities_paise} />
        <BalanceGroup title="Equity" rows={b.groups.equity} sum={b.totals.equity_paise} />
      </div>
      <div className={`md:col-span-2 bg-white border-2 rounded-2xl p-4 flex flex-wrap gap-4 text-sm ${b.balanced ? "border-emerald-500" : "border-red-500"}`}>
        <div><span className="text-stone-500">Assets:</span> <b className="font-mono">{formatMoney(b.totals.assets_paise, cur)}</b></div>
        <div><span className="text-stone-500">Liabilities + Equity + Retained:</span> <b className="font-mono">{formatMoney(b.totals.liabilities_paise + b.totals.equity_plus_retained_paise, cur)}</b></div>
        <div><span className="text-stone-500">Net income:</span> <b className={`font-mono ${b.totals.net_income_paise >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatMoney(b.totals.net_income_paise, cur)}</b></div>
        <div className="ml-auto"><span className="text-stone-500">Status:</span> <b className={b.balanced ? "text-emerald-700" : "text-red-700"}>{b.balanced ? "✓ Balanced" : "✗ Off by " + formatMoney(Math.abs(b.imbalance_paise), cur)}</b></div>
      </div>
    </div>
  );
}

function InventoryTab() {
  const cur = useCurrency();
  const [inv, setInv] = useState(null);
  useEffect(() => { api.get("/erp/reports/inventory").then(r => setInv(r.data)).catch(() => {}); }, []);
  if (!inv) return <Loader2 className="animate-spin mx-auto text-stone-400" />;
  return (
    <div className="space-y-3" data-testid="erp-inventory">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Inventory</div>
        <ExportButtons endpoint="/erp/reports/inventory" filename="inventory" testIdPrefix="erp-inventory-export" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="SKUs" val={inv.totals.distinct_skus} />
        <Stat label="Total units" val={inv.totals.total_units} />
        <Stat label="Cost value" val={formatMoney(inv.totals.total_cost_value_paise, cur)} />
        <Stat label="Potential profit" val={formatMoney(inv.totals.potential_gross_profit_paise, cur)} color="text-emerald-700" />
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
            <tr><th className="px-3 py-2 text-left">Product</th><th className="text-right">Stock</th><th className="text-right">Cost</th><th className="text-right">Sell</th><th className="text-right">Value</th></tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {inv.products.map(p => (<tr key={p.product_id}><td className="px-3 py-2">{p.name}</td><td className="text-right font-mono">{p.stock} {p.unit}</td><td className="text-right font-mono text-stone-600">{formatMoney(p.cost_paise, cur)}</td><td className="text-right font-mono text-emerald-700">{formatMoney(p.sell_paise, cur)}</td><td className="text-right font-mono font-bold">{formatMoney(p.stock * p.cost_paise, cur)}</td></tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HoldingsTab() {
  const cur = useCurrency();
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/erp/reports/holdings").then(r => setH(r.data)).catch(() => {}); }, []);
  if (!h) return <Loader2 className="animate-spin mx-auto text-stone-400" />;
  return (
    <div className="space-y-3" data-testid="erp-holdings">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Holdings</div>
        <ExportButtons endpoint="/erp/reports/holdings" filename="holdings" testIdPrefix="erp-holdings-export" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Cost value" val={formatMoney(h.total_cost_paise, cur)} />
        <Stat label="Market value" val={formatMoney(h.total_sell_paise, cur)} />
        <Stat label="Unrealised profit" val={formatMoney(h.total_sell_paise - h.total_cost_paise, cur)} color="text-emerald-700" />
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
            <tr><th className="px-3 py-2 text-left">Product</th><th className="text-right">Qty</th><th className="text-right">Cost value</th><th className="text-right">Sell value</th><th className="text-right">Gain</th></tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {h.holdings.map(x => (<tr key={x.product_id}><td className="px-3 py-2">{x.name}</td><td className="text-right font-mono">{x.quantity} {x.unit}</td><td className="text-right font-mono">{formatMoney(x.cost_value_paise, cur)}</td><td className="text-right font-mono text-emerald-700">{formatMoney(x.sell_value_paise, cur)}</td><td className="text-right font-mono font-bold text-emerald-700">{formatMoney(x.unrealised_gain_paise, cur)}</td></tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, val, color = "text-stone-900" }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">{label}</div>
      <div className={`text-lg font-black font-mono ${color}`}>{val}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// P2P Bills — counterparty lookup via NVN/mobile/email/aadhar, issue & settle.
// -----------------------------------------------------------------------------

/** Downloads a P2P bill as PDF or JPG via the authenticated backend endpoints. */
function BillDownloadButtons({ billId }) {
  const [busy, setBusy] = useState(null);           // "pdf" | "jpg" | null
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const mime = fmt === "pdf" ? "application/pdf" : "image/jpeg";
      const r = await api.get(`/erp/bill/${billId}/${fmt}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bill-${billId}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`${fmt.toUpperCase()} download failed`);
    } finally { setBusy(null); }
  };
  return (
    <div className="inline-flex gap-1 mr-1">
      <button
        onClick={() => download("pdf")}
        disabled={busy === "pdf"}
        className="text-[11px] font-bold text-stone-700 hover:text-red-700 border border-stone-200 hover:border-red-400 rounded-full px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60"
        data-testid={`p2p-bill-pdf-${billId}`}
        title="Download PDF"
      >
        {busy === "pdf" ? <Loader2 size={11} className="animate-spin"/> : <FileText size={11}/>} PDF
      </button>
      <button
        onClick={() => download("jpg")}
        disabled={busy === "jpg"}
        className="text-[11px] font-bold text-stone-700 hover:text-indigo-700 border border-stone-200 hover:border-indigo-400 rounded-full px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60"
        data-testid={`p2p-bill-jpg-${billId}`}
        title="Download JPG"
      >
        {busy === "jpg" ? <Loader2 size={11} className="animate-spin"/> : <ImageIcon size={11}/>} JPG
      </button>
    </div>
  );
}

function P2PTab() {
  const { user } = useAuth();
  const [sub, setSub] = useState("issue");                      // issue | issued | received
  const [issued, setIssued] = useState([]);
  const [received, setReceived] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      api.get("/erp/bills/issued").then(r => setIssued(r.data)).catch(() => {}),
      api.get("/erp/bills/received").then(r => setReceived(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const settle = async (bill_id, method = "cash") => {
    if (!window.confirm(`Mark bill ${bill_id} as settled (${method})? Both ledgers will post the payment.`)) return;
    try {
      await api.post(`/erp/bill/${bill_id}/settle`, { method });
      toast.success("Bill settled · both ledgers updated");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Settle failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="erp-p2p">
      <div className="flex gap-1 border-b border-stone-200">
        {[
          { k: "issue",    l: "Issue new bill", n: null },
          { k: "issued",   l: "Bills I issued", n: issued.length },
          { k: "received", l: "Bills I received", n: received.length },
        ].map(t => {
          const active = sub === t.k;
          return (
            <button
              key={t.k}
              onClick={() => setSub(t.k)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border-b-2 -mb-px transition-colors ${active ? "text-emerald-700 border-emerald-700" : "text-stone-500 border-transparent hover:text-stone-800"}`}
              data-testid={`p2p-sub-${t.k}`}
            >
              {t.l} {t.n != null && <span className="text-[10px] font-mono bg-stone-100 rounded-full px-1.5">{t.n}</span>}
            </button>
          );
        })}
      </div>

      {sub === "issue" && <IssueBillForm onIssued={() => { setSub("issued"); refresh(); }} />}
      {sub === "issued" && (
        <BillTable rows={issued} loading={loading} myId={user?.user_id} kind="issued" onSettle={settle} empty="You haven't issued any bills yet." />
      )}
      {sub === "received" && (
        <BillTable rows={received} loading={loading} myId={user?.user_id} kind="received" onSettle={settle} empty="No bills have been raised against you." />
      )}
    </div>
  );
}

function IssueBillForm({ onIssued }) {
  const cur = useCurrency();
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);             // {user_id, name, nvn, matched_by, ...}
  const [offLedgerName, setOffLedgerName] = useState("");
  const [lines, setLines] = useState([{ description: "", quantity: "1", unit_price: "", tax_pct: "0" }]);
  const [paymentAccount, setPaymentAccount] = useState("debtors");
  const [paymentTerms, setPaymentTerms] = useState("on_receipt");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const doLookup = async () => {
    const term = q.trim();
    if (term.length < 3) return toast.error("Enter at least 3 characters (NVN, mobile, email or aadhar)");
    setSearching(true); setMatches([]); setSelected(null);
    try {
      const r = await api.get("/erp/lookup-user", { params: { q: term } });
      setMatches(r.data.matches || []);
      if (!r.data.matches?.length) {
        toast.message("No registered user found — you can still issue as an off-ledger bill below.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    } finally {
      setSearching(false);
    }
  };

  const totals = React.useMemo(() => {
    let sub = 0, tax = 0;
    lines.forEach(l => {
      const price_minor = toMinor(l.unit_price, cur.decimals);
      const qty = parseInt(l.quantity) || 0;
      const s = price_minor * qty;
      const t = Math.round(s * (parseFloat(l.tax_pct) || 0) / 100);
      sub += s; tax += t;
    });
    return { sub, tax, total: sub + tax };
  }, [lines, cur.decimals]);

  const submit = async (e) => {
    e.preventDefault();
    const validLines = lines
      .filter(l => l.description && parseInt(l.quantity) > 0 && toMinor(l.unit_price, cur.decimals) > 0)
      .map(l => ({
        description: l.description,
        quantity: parseInt(l.quantity),
        unit_price_paise: toMinor(l.unit_price, cur.decimals),
        tax_pct: parseFloat(l.tax_pct) || 0,
      }));
    if (!validLines.length) return toast.error("Add at least one valid line (description, qty, price).");
    if (!selected && !offLedgerName.trim()) return toast.error("Search & select a counterparty, or enter an off-ledger name.");

    setSaving(true);
    try {
      const payload = {
        lines: validLines,
        payment_account: paymentAccount,
        payment_terms: paymentTerms,
        note,
      };
      if (selected) {
        if (selected.matched_by === "nvn" || selected.nvn) payload.counterparty_nvn = selected.nvn;
        else if (selected.matched_by === "mobile") payload.counterparty_mobile = selected.mobile;
        else if (selected.matched_by === "email") payload.counterparty_email = selected.email;
        else if (selected.matched_by === "aadhar") payload.counterparty_aadhar = q.trim();
        payload.counterparty_name = selected.name;
      } else {
        payload.counterparty_name = offLedgerName.trim();
      }
      const r = await api.post("/erp/bill/issue", payload);
      toast.success(
        r.data.counterparty_notified_in_ledger
          ? `Bill issued · both ledgers updated (${formatMoney(r.data.total_paise, cur)})`
          : `Bill issued off-ledger (${formatMoney(r.data.total_paise, cur)})`
      );
      // Reset
      setQ(""); setMatches([]); setSelected(null); setOffLedgerName("");
      setLines([{ description: "", quantity: "1", unit_price: "", tax_pct: "0" }]);
      setNote("");
      onIssued?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to issue bill");
    } finally {
      setSaving(false);
    }
  };

  const step = cur.decimals > 0 ? `0.${"0".repeat(Math.max(0, cur.decimals - 1))}1` : "1";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="p2p-issue-form">
      {/* Counterparty lookup */}
      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700 mb-2 flex items-center gap-1.5">
          <SearchIcon size={12} /> Step 1 · Find the counterparty
        </div>
        <div className="flex gap-2">
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), doLookup())}
            placeholder="Enter NVN, mobile, email, or aadhar"
            className="rounded-xl"
            data-testid="p2p-lookup-input"
          />
          <Button type="button" onClick={doLookup} disabled={searching} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="p2p-lookup-btn">
            {searching ? <Loader2 size={14} className="animate-spin" /> : "Search"}
          </Button>
        </div>

        {matches.length > 0 && !selected && (
          <div className="mt-3 space-y-2" data-testid="p2p-matches">
            {matches.map(m => (
              <button
                key={m.user_id + m.match_field}
                type="button"
                onClick={() => setSelected(m)}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-stone-200 hover:border-emerald-600 hover:bg-emerald-50 text-left transition-colors"
                data-testid={`p2p-match-${m.user_id}`}
              >
                <div className="w-10 h-10 rounded-full bg-stone-100 grid place-items-center overflow-hidden shrink-0">
                  {m.picture ? <img loading="lazy" src={m.picture} alt="" className="w-full h-full object-cover" /> : <UserIcon size={16} className="text-stone-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-stone-900 truncate">{m.name || "(unnamed)"}</div>
                  <div className="text-[11px] text-stone-500 font-mono truncate">
                    {m.nvn && <>NVN {m.nvn}</>} {m.mobile && <>· {m.mobile}</>} {m.email && <>· {m.email}</>}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                  match · {m.match_field}
                </span>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-3" data-testid="p2p-selected">
            <div className="w-10 h-10 rounded-full bg-white grid place-items-center overflow-hidden shrink-0 border border-emerald-200">
              {selected.picture ? <img loading="lazy" src={selected.picture} alt="" className="w-full h-full object-cover" /> : <UserIcon size={16} className="text-emerald-700" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-emerald-900 truncate">Billing to: {selected.name}</div>
              <div className="text-[11px] text-emerald-700 font-mono truncate">
                {selected.nvn && <>NVN {selected.nvn}</>} · matched by {selected.matched_by || selected.match_field}
              </div>
            </div>
            <button type="button" onClick={() => setSelected(null)} className="text-emerald-700 hover:text-red-600" aria-label="Clear selection" data-testid="p2p-clear-selected">
              <X size={16} />
            </button>
          </div>
        )}

        {matches.length === 0 && q && !searching && !selected && (
          <div className="mt-3 p-3 rounded-xl border border-dashed border-stone-300 text-sm">
            <div className="text-stone-600 mb-1">No registered user matched. You can still issue an <b>off-ledger</b> bill (only your books post):</div>
            <Input
              value={offLedgerName}
              onChange={e => setOffLedgerName(e.target.value)}
              placeholder="Counterparty display name (e.g. Walk-in customer)"
              className="rounded-xl"
              data-testid="p2p-offledger-name"
            />
          </div>
        )}
      </div>

      {/* Line items */}
      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700 mb-3 flex items-center gap-1.5">
          <ReceiptText size={12} /> Step 2 · Line items <span className="ml-auto text-stone-500 normal-case tracking-normal">Prices in <b className="font-mono">{cur.symbol} {cur.code}</b></span>
        </div>
        <div className="space-y-2">
          {lines.map((l, idx) => (
            <div key={idx} className="grid md:grid-cols-12 gap-2 items-center" data-testid={`p2p-line-${idx}`}>
              <Input
                value={l.description}
                onChange={e => setLines(ll => ll.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))}
                placeholder="Description (e.g. Consulting hours, Repair work)"
                className="rounded-xl md:col-span-5"
                data-testid={`p2p-line-desc-${idx}`}
              />
              <Input
                type="number" min="1"
                value={l.quantity}
                onChange={e => setLines(ll => ll.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))}
                placeholder="Qty"
                className="rounded-xl md:col-span-1"
                data-testid={`p2p-line-qty-${idx}`}
              />
              <div className="md:col-span-2">
                <MoneyInput
                  value={l.unit_price}
                  onChange={v => setLines(ll => ll.map((x, i) => i === idx ? { ...x, unit_price: v } : x))}
                  placeholder={`Price (${cur.symbol})`}
                  step={step}
                  testId={`p2p-line-price-${idx}`}
                />
              </div>
              <Input
                type="number" min="0" step="0.1"
                value={l.tax_pct}
                onChange={e => setLines(ll => ll.map((x, i) => i === idx ? { ...x, tax_pct: e.target.value } : x))}
                placeholder="Tax %"
                className="rounded-xl md:col-span-1"
                data-testid={`p2p-line-tax-${idx}`}
              />
              <div className="md:col-span-2 text-right font-mono text-sm text-stone-700">
                {formatMoney(
                  Math.round(toMinor(l.unit_price, cur.decimals) * (parseInt(l.quantity) || 0) * (1 + (parseFloat(l.tax_pct) || 0) / 100)),
                  cur
                )}
              </div>
              <button
                type="button"
                onClick={() => setLines(ll => ll.filter((_, i) => i !== idx))}
                disabled={lines.length === 1}
                className="md:col-span-1 justify-self-end text-stone-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Remove line"
                data-testid={`p2p-line-remove-${idx}`}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setLines(ll => [...ll, { description: "", quantity: "1", unit_price: "", tax_pct: "0" }])}
          className="mt-3 rounded-full text-xs"
          data-testid="p2p-add-line"
        >
          <Plus size={12} className="mr-1.5" /> Add another line
        </Button>

        <div className="mt-4 pt-4 border-t border-stone-100 flex flex-wrap items-center justify-end gap-4 text-sm">
          <div className="text-stone-600">Subtotal <b className="font-mono ml-1">{formatMoney(totals.sub, cur)}</b></div>
          <div className="text-stone-600">Tax <b className="font-mono ml-1">{formatMoney(totals.tax, cur)}</b></div>
          <div className="text-emerald-800 text-base">Total <b className="font-mono ml-1" data-testid="p2p-total">{formatMoney(totals.total, cur)}</b></div>
        </div>
      </div>

      {/* Payment terms */}
      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700 mb-3 flex items-center gap-1.5">
          <IndianRupee size={12} /> Step 3 · Terms & finalise
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Payment account</label>
            <Select value={paymentAccount} onValueChange={setPaymentAccount}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="p2p-payment-account"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="debtors">On credit (Debtors)</SelectItem>
                <SelectItem value="cash">Paid in cash</SelectItem>
                <SelectItem value="bank">Paid to bank</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Payment terms</label>
            <Select value={paymentTerms} onValueChange={setPaymentTerms}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="p2p-payment-terms"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="on_receipt">On receipt</SelectItem>
                <SelectItem value="net_15">Net 15 days</SelectItem>
                <SelectItem value="net_30">Net 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Note (optional)</label>
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Reference / job code" className="rounded-xl mt-1" data-testid="p2p-note" />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button type="submit" disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-6" data-testid="p2p-submit">
            {saving ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Issuing…</> : <><CheckCircle2 size={14} className="mr-1.5" /> Issue bill {formatMoney(totals.total, cur)}</>}
          </Button>
        </div>
      </div>
    </form>
  );
}

function BillTable({ rows, loading, myId, kind, onSettle, empty }) {
  const cur = useCurrency();
  if (loading) return <Loader2 className="animate-spin mx-auto text-stone-400 mt-8" />;
  if (!rows.length) return <div className="p-10 text-center text-stone-500 text-sm bg-white border border-stone-200 rounded-2xl">{empty}</div>;
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={`p2p-table-${kind}`}>
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
          <tr>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="text-left">Bill ID</th>
            <th className="text-left">{kind === "issued" ? "To" : "From"}</th>
            <th className="text-left">Terms</th>
            <th className="text-right">Total</th>
            <th className="text-center">Status</th>
            <th className="text-right pr-3">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {rows.map(b => {
            const partyName = kind === "issued" ? b.counterparty_name : b.issuer_name;
            const canSettle = b.status !== "settled" && (b.issuer_user_id === myId || b.counterparty_user_id === myId);
            return (
              <tr key={b.bill_id} className="hover:bg-stone-50" data-testid={`p2p-bill-${b.bill_id}`}>
                <td className="px-3 py-2 text-xs text-stone-500 font-mono whitespace-nowrap">{(b.created_at || "").slice(0, 10)}</td>
                <td className="text-xs font-mono text-stone-700">{b.bill_id}</td>
                <td className="text-stone-900 font-bold">
                  {partyName || <span className="text-stone-400 italic">(off-ledger)</span>}
                  {b.off_ledger && <span className="ml-1.5 text-[9px] font-black uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5">off-ledger</span>}
                </td>
                <td className="text-xs text-stone-500 capitalize">{(b.payment_terms || "").replace("_", " ")}</td>
                <td className="text-right font-mono font-bold">{formatMoney(b.total_paise, cur)}</td>
                <td className="text-center">
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 ${b.status === "settled" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {b.status}
                  </span>
                </td>
                <td className="text-right pr-3">
                  <div className="inline-flex gap-1 items-center">
                    <BillDownloadButtons billId={b.bill_id} />
                    {canSettle ? (
                      <>
                        <button
                          onClick={() => onSettle(b.bill_id, "cash")}
                          className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 border border-emerald-200 hover:border-emerald-500 rounded-full px-2.5 py-1"
                          data-testid={`p2p-settle-cash-${b.bill_id}`}
                        >
                          Settle · cash
                        </button>
                        <button
                          onClick={() => onSettle(b.bill_id, "bank")}
                          className="text-[11px] font-bold text-blue-700 hover:text-blue-900 border border-blue-200 hover:border-blue-500 rounded-full px-2.5 py-1"
                          data-testid={`p2p-settle-bank-${b.bill_id}`}
                        >
                          Settle · bank
                        </button>
                      </>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
